/**
 * Read-only diagnostics exposed on `window.__sonicforgeDebug` so browser tests can
 * verify resource lifecycles (audio contexts, Pixi applications). Contains no user data.
 */
export interface DebugCounters {
  pixiAppsCreated: number;
  pixiAppsDestroyed: number;
  audioEnginesStarted: number;
  audioEnginesDisposed: number;
  audioState: string;
  animationFrame: number;
  /** Phase 5B.0: whether this build contains the test-only hooks (false in the production build). */
  testHooks: boolean;
  /** Phase 3A: live renderer resources (display objects, textures). */
  sceneStats?: () => {
    objects: number;
    textures: number;
    textureBytes: number;
    redraws: number;
    transformUpdates: number;
    evaluations: number;
    poseUpdates: number;
    evaluationMs: number;
    frame: number;
    guides: number;
    guidePoints: number;
    /** Phase 5B.1: live Look v1 resources (shared blur filters, objects with effects, gradient fills). */
    effectFilters?: number;
    effectObjects?: number;
    gradients?: number;
    grade?: boolean;
    vignette?: boolean;
  } | null;
  /** Phase 3B: displayed frame and animated channel values (object ids and numbers only). */
  animationState?: () => { frame: number; playing: boolean; values: Record<string, Record<string, number>> } | null;
  /** Phase 4A: the Sync preview — session position, displayed values (animation + reactions) and effects. */
  syncState?: () => {
    seconds: number;
    /** Phase 5B.0: the position of the frame actually drawn (seconds is the live clock). */
    shownSeconds?: number;
    frame: number;
    playing: boolean;
    status: string;
    source: string | null;
    values: Record<string, Record<string, number>>;
    effects: { flash: number; background: number; shakeX: number; shakeY: number } | null;
    reactionEvaluations: number;
    reactingObjects: number;
    /** Phase 4C: visualizer layers shown (per layer: id, type, measured or event-driven, a value digest). */
    visualizer?: { fallback: string[]; flash: number; layers: { id: string; type: string; mode: string; value: number }[] } | null;
  } | null;
  /** Phase 4A: extracted and manual events of the selected session (ids, categories, timing; no user data). */
  syncEvents?: () => { id: string; category: string; tick: number; seconds: number; velocity: number }[] | null;
  /** Phase 4A: the session clock and the audio transport's own position at the same audible instant (seconds). */
  syncDrift?: () => { clock: number; transport: number | null; source: string | null } | null;
  /** Phase 4B: the last video export's state and report (settings, timings, sizes; no user data). */
  videoExport?: () => unknown;
  /*
   * Test hooks below: present only when built with test hooks (dev server, unit tests,
   * `npm run build:e2e`); absent from the production build (`__SONICFORGE_TEST_HOOKS__`,
   * checked by `npm run check:hooks`). The two Phase 4B probes moved here in Phase 5C.1.
   */
  /** Phase 4B: a deterministic export frame renderer for tests (renders frames, returns 64×36 averaged pixels). */
  videoFrameProbe?: (seconds: number[], width: number, height: number) => Promise<{ seconds: number; rgba: number[] }[]>;
  /** Phase 4B: the session audio as export renders it before encoding (1 ms RMS envelope of channel 0). */
  videoAudioProbe?: (start: number, seconds: number) => Promise<number[]>;
  /** Phase 5B.0: full-resolution export frames (PNG data URLs) from the deterministic export renderer. */
  videoFrameCapture?: (seconds: number[], width: number, height: number) => Promise<string[]>;
  /** Phase 5B.0: export renderer timing: creation, then each frame drawn and read back (forces the GPU to finish). */
  videoFrameBench?: (seconds: number[], width: number, height: number) => Promise<{ createMs: number; frameMs: number[] }>;
  /** Phase 5B.0: seeks the Animation workspace clock; returns the synchronous seek + draw time (ms). */
  animationSeek?: (frame: number) => number;
  /** Phase 5B.0: seeks the Sync session like the UI does; returns the synchronous seek + draw time while paused (ms). */
  syncSeek?: (seconds: number) => number;
  /**
   * Phase 4B test override: lower the video export's hard output limit and the real-time
   * re-mux limit (bytes) so browser tests can exercise them without huge files. Unset in
   * normal use.
   */
  videoOutputLimit?: number;
  videoRemuxLimit?: number;
  /** Phase 4C: the measured-analysis cache state (status, analysed seconds, timings, analyses computed). */
  visualizerAnalysis?: () => { status: string; seconds?: number; renderMs?: number; analyzeMs?: number; computed: number; started: number };
  /** Phase 2: decoded-audio cache and voice counts (no user data). */
  audioStats?: () => { cacheBytes: number; cacheEntries: number; decodes: number; evictions: number; liveVoices: number };
}

export const debugCounters: DebugCounters = {
  pixiAppsCreated: 0,
  pixiAppsDestroyed: 0,
  audioEnginesStarted: 0,
  audioEnginesDisposed: 0,
  audioState: 'idle',
  animationFrame: 0,
  testHooks: __SONICFORGE_TEST_HOOKS__,
};

declare global {
  interface Window {
    __sonicforgeDebug?: DebugCounters;
  }
}

if (typeof window !== 'undefined') window.__sonicforgeDebug = debugCounters;
